import React, { useContext, useEffect, useState } from 'react';
import ErrorScreen from '../../components/lm-d-s/errorScreen';
import styles from '../../components/lm-d-s/index.module.scss';
import { ScreensContext } from '../../contexts/screensContext';
import ConsentBannerScreen from './components/consent-banner-screen';
import PhoneScreen from './components/phone-screen';
import CompanyInfoScreen from './components/company-info-screen';
import FaqScreen from './components/faq-screen';
import CookieManagementScreen from './components/cookie-management-screen';
import OtpScreen from './components/otp-screen';
import FaceCaptureScreen from './components/face-capture-screen';
import WelcomeAgeVerificationScreen from './components/welcome-age-verification-screen';
import WelcomeFormScreen from './components/welcome-form-screen';
import FormScreen from './components/form-screen';
import AttemptsExhausted from './components/attempts-exhausted';
import useTelegramLink from '../../hooks/useTelegramLink';
import NotRealFaceScreen from './components/not-real-face-screen';
import YoungAgeScreen from './components/young-age-screen';
import ErrorPage500 from '../../components/ua-brand/error-page-500';
import OtpScreenRegistration from './components/otp-screen-registration';
import AlreadyRegistered from './components/already-registered';
import { DataLayerEventContext } from '../../contexts/dataLayerEventContext';

const isQrCode = sessionStorage.getItem('isQrCode');
const utm_campaign = sessionStorage.getItem('utm_campaign');

const Lm1TestRegistrationFlowWrapper = ({
  children,
  onComplete = null,
  handleSkipRegistration = null,
  isWithoutRegistration = false,
  withoutCookieBanner = false,
  externalScreenNumber = null,
  setExternalScreenNumber = null
}) => {
  const { telegramLink } = useTelegramLink();
  const { sendEvent } = useContext(DataLayerEventContext);
  const initialScreen = withoutCookieBanner ? 1 : 0;
  const [internalScreenNumber, setInternalScreenNumber] =
    useState(initialScreen);
  const screenNumber = setExternalScreenNumber
    ? (externalScreenNumber ?? initialScreen)
    : internalScreenNumber;

  const handleSetScreenNumber = (number) => {
    if (setExternalScreenNumber) {
      setExternalScreenNumber(number);
    } else {
      setInternalScreenNumber(number);
    }
  };
  const [flowCompleted, setFlowCompleted] = useState(false);
  const [endSessionType, setEndSessionType] = useState('SESSION_END');
  const [isDisplayedAll, setIsDisplayedAll] = useState(false);
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    if (flowCompleted && onComplete) onComplete();
  }, [flowCompleted]);

  if (flowCompleted) {
    return onComplete ? null : <>{children}</>;
  }

  const getScreen = (number, cb) => {
    switch (number) {
      case 0: {
        return (
          <ConsentBannerScreen
            onAcceptAll={() => handleSetScreenNumber(1)}
            onLearnMore={() => handleSetScreenNumber(4)}
          >
            <PhoneScreen
              isBackground
              setScreenNumber={() => {}}
              handleNextScreen={() => {}}
            />
          </ConsentBannerScreen>
        );
      }
      case 1: {
        return (
          <PhoneScreen
            setScreenNumber={cb}
            handleNextScreen={() => handleSetScreenNumber(5)}
          />
        );
      }
      case 2: {
        return (
          <CompanyInfoScreen
            handleBackButtonClick={() => handleSetScreenNumber(1)}
          />
        );
      }
      case 3: {
        return (
          <FaqScreen
            setScreenNumber={handleSetScreenNumber}
            handleBackButtonClick={() => handleSetScreenNumber(1)}
          />
        );
      }
      case 4: {
        return (
          <CookieManagementScreen setScreenNumber={handleSetScreenNumber} />
        );
      }
      case 5: {
        return (
          <OtpScreen
            isWithoutRegistration={isWithoutRegistration}
            setScreenNumber={handleSetScreenNumber}
            attemptsExhaustedChangeScreen={() => handleSetScreenNumber(10)}
            handleChangePhoneNumber={() => handleSetScreenNumber(1)}
            handleRedirectToChildren={() => setFlowCompleted(true)}
          />
        );
      }
      case 6: {
        return (
          <WelcomeAgeVerificationScreen
            setScreenNumber={handleSetScreenNumber}
            handleNextVerificationScreen={() => handleSetScreenNumber(7)}
          />
        );
      }
      case 7: {
        return (
          <FaceCaptureScreen
            setScreenNumber={handleSetScreenNumber}
            handleChangePhoneNumber={() => handleSetScreenNumber(1)}
            successSendOtp={() => handleSetScreenNumber(5)}
            isWithoutRegistration={isWithoutRegistration}
            handleRedirectToChildren={() => setFlowCompleted(true)}
          />
        );
      }
      case 8: {
        return (
          <WelcomeFormScreen
            setScreenNumber={handleSetScreenNumber}
            handleNextScreen={() => {
              sendEvent('registration');
              handleSetScreenNumber(9);
            }}
            handleSkipScreen={() => {
              sendEvent('skip_registration');
              if (handleSkipRegistration) handleSkipRegistration();
              setFlowCompleted(true);
            }}
          />
        );
      }
      case 9: {
        return (
          <FormScreen
            utm_campaign={utm_campaign}
            setScreenNumber={handleSetScreenNumber}
            backButtonClick={() => handleSetScreenNumber(8)}
            setFormData={(data) => setFormData(data)}
            formData={formData}
            completeFlow={() => setFlowCompleted(true)}
          />
        );
      }
      case 10: {
        return (
          <AttemptsExhausted
            setScreenNumber={handleSetScreenNumber}
            checkPhoneNumber={() => handleSetScreenNumber(1)}
            telegramLink={telegramLink}
          />
        );
      }
      case 11: {
        return (
          <YoungAgeScreen backButtonClick={() => handleSetScreenNumber(7)} />
        );
      }
      case 12: {
        return (
          <OtpScreenRegistration
            setScreenNumber={handleSetScreenNumber}
            telegramLink={telegramLink}
            handleChangePhoneNumber={() => handleSetScreenNumber(9)}
            successSendOtp={() => setFlowCompleted(true)}
            attemptsExhaustedChangeScreen={() => handleSetScreenNumber(10)}
          />
        );
      }
      case 13: {
        return (
          <AlreadyRegistered
            telegramLink={''}
            setScreenNumber={handleSetScreenNumber}
            handleNextButtonClick={() => setFlowCompleted(true)}
            utm_campaign={isQrCode}
          />
        );
      }
      case 16: {
        return (
          <NotRealFaceScreen
            setScreenNumber={handleSetScreenNumber}
            handleRepeatButtonClick={() => handleSetScreenNumber(7)}
          />
        );
      }
      case 500: {
        return <ErrorPage500 setScreenNumber={cb} />;
      }
      default: {
        return <ErrorScreen setScreenNumber={cb} />;
      }
    }
  };

  return (
    <div className={styles.pageContainer} id="registration-screen-container">
      <div className={styles.contentContainer}>
        <ScreensContext.Provider
          value={{
            endSessionType,
            setEndSessionType,
            isDisplayedAll,
            setIsDisplayedAll
          }}
        >
          {getScreen(screenNumber, handleSetScreenNumber)}
        </ScreensContext.Provider>
      </div>
    </div>
  );
};

export default Lm1TestRegistrationFlowWrapper;
